import React, { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "./Sidebar";
import "./adminRoutes.css";

export default function AdminPickupLocations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("ALL"); // ALL | PENDING | APPROVED | REJECTED
  const [searchTerm, setSearchTerm] = useState("");

  // Reject Modal
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const [feedback, setFeedback] = useState({ type: "", msg: "" });

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/admin/fulfillment/locations", {
        withCredentials: true
      });
      if (res.data?.locations) {
        setLocations(res.data.locations);
      }
    } catch (err) {
      setFeedback({ type: "error", msg: err.response?.data?.msg || "Failed to load pickup locations." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleApprove = async (id) => {
    try {
      setActionLoading(true);
      const res = await axios.put(`/api/admin/fulfillment/locations/${id}/approve`, {}, { withCredentials: true });
      setFeedback({ type: "success", msg: res.data?.message || "Pickup location approved successfully." });
      fetchLocations();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to approve pickup location.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!selectedLocation || !rejectionReason.trim()) {
      return alert("Please enter a rejection reason.");
    }

    try {
      setActionLoading(true);
      const res = await axios.put(
        `/api/admin/fulfillment/locations/${selectedLocation._id}/reject`,
        { reason: rejectionReason.trim() },
        { withCredentials: true }
      );
      setFeedback({ type: "success", msg: res.data?.message || "Pickup location rejected." });
      setRejectModalOpen(false);
      setSelectedLocation(null);
      setRejectionReason("");
      fetchLocations();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to reject pickup location.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      setActionLoading(true);
      const res = await axios.put(`/api/admin/fulfillment/locations/${id}/toggle`, {}, { withCredentials: true });
      setFeedback({ type: "success", msg: res.data?.message || "Location status toggled." });
      fetchLocations();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to toggle location.");
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = locations.filter((l) => {
    const term = searchTerm.toLowerCase();
    const matchesTab = activeTab === "ALL" ? true : l.status === activeTab;
    const matchesSearch =
      l.name?.toLowerCase().includes(term) ||
      l.address?.city?.toLowerCase().includes(term) ||
      l.address?.street?.toLowerCase().includes(term) ||
      l.vendor?.firstName?.toLowerCase().includes(term) ||
      l.store?.name?.toLowerCase().includes(term);

    return matchesTab && matchesSearch;
  });

  const pendingCount = locations.filter(l => l.status === "PENDING").length;
  const approvedCount = locations.filter(l => l.status === "APPROVED").length;
  const rejectedCount = locations.filter(l => l.status === "REJECTED").length;

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboardContainer" style={{ padding: "28px", background: "#f8fafc", minHeight: "100vh" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", marginBottom: "24px" }}>
          <div>
            <h1 style={{ fontSize: "24px", fontWeight: "900", color: "#0f172a", margin: "0 0 4px" }}>
              📍 Vendor Pickup Locations &amp; Warehouses
            </h1>
            <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
              Review and approve secondary store outlets, warehouses, and manage active status for order fulfillment.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchLocations}
            className="btn btn-outline-primary"
            style={{ fontSize: "13px", fontWeight: "700", padding: "8px 16px" }}
          >
            ↻ Refresh Locations
          </button>
        </div>

        {/* Feedback Alert */}
        {feedback.msg && (
          <div
            style={{
              background: feedback.type === "success" ? "#f0fdf4" : "#fef2f2",
              border: `1.5px solid ${feedback.type === "success" ? "#bbf7d0" : "#fecaca"}`,
              color: feedback.type === "success" ? "#166534" : "#991b1b",
              padding: "12px 18px",
              borderRadius: "10px",
              marginBottom: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "13.5px",
              fontWeight: "700"
            }}
          >
            <span>{feedback.msg}</span>
            <button type="button" onClick={() => setFeedback({ type: "", msg: "" })} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "16px" }}>✕</button>
          </div>
        )}

        {/* Top Summary KPI Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Total Locations</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#0f172a", marginTop: "4px" }}>{locations.length}</div>
          </div>
          <div style={{ background: "#ffffff", border: "1.5px solid #fef3c7", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#b45309", textTransform: "uppercase" }}>● Pending Approval</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#b45309", marginTop: "4px" }}>{pendingCount}</div>
          </div>
          <div style={{ background: "#ffffff", border: "1.5px solid #dcfce7", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#166534", textTransform: "uppercase" }}>✓ Approved Hubs</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#16a34a", marginTop: "4px" }}>{approvedCount}</div>
          </div>
          <div style={{ background: "#ffffff", border: "1.5px solid #fee2e2", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#991b1b", textTransform: "uppercase" }}>✕ Rejected</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#dc2626", marginTop: "4px" }}>{rejectedCount}</div>
          </div>
        </div>

        {/* Filter Controls & Search */}
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "18px 22px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "14px" }}>
          {/* Status Tabs */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {["ALL", "PENDING", "APPROVED", "REJECTED"].map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => setActiveTab(st)}
                style={{
                  padding: "6px 14px",
                  fontSize: "12.5px",
                  fontWeight: "800",
                  borderRadius: "8px",
                  border: "none",
                  background: activeTab === st ? "#0f172a" : "#f1f5f9",
                  color: activeTab === st ? "#ffffff" : "#475569",
                  cursor: "pointer"
                }}
              >
                {st === "ALL" ? "All Hubs" : st === "PENDING" ? "⏳ Pending" : st === "APPROVED" ? "✓ Approved" : "✕ Rejected"}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div style={{ minWidth: "260px" }}>
            <input
              type="text"
              placeholder="Search by Location, City, Vendor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13px" }}
            />
          </div>
        </div>

        {/* Locations Grid */}
        {loading ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>Loading pickup locations...</div>
        ) : filtered.length > 0 ? (
          <div className="row g-3">
            {filtered.map((loc) => (
              <div key={loc._id} className="col-lg-6">
                <div style={{ background: "#ffffff", border: "1.5px solid #e2e8f0", borderRadius: "14px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div>
                    {/* Top Row: Title + Status Pill */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                      <div>
                        <span style={{ fontSize: "16px", fontWeight: "900", color: "#0f172a" }}>{loc.name}</span>
                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                          Store: <strong>{loc.store?.name || "Merchant Store"}</strong> &bull; Vendor: {loc.vendor?.firstName} {loc.vendor?.lastName}
                        </div>
                      </div>

                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 800,
                          padding: "3px 8px",
                          borderRadius: "99px",
                          background: loc.status === "APPROVED" ? (loc.isActive ? "#dcfce7" : "#f1f5f9") : loc.status === "REJECTED" ? "#fee2e2" : "#fef3c7",
                          color: loc.status === "APPROVED" ? (loc.isActive ? "#166534" : "#64748b") : loc.status === "REJECTED" ? "#991b1b" : "#92400e",
                          border: `1px solid ${loc.status === "APPROVED" ? (loc.isActive ? "#bbf7d0" : "#cbd5e1") : loc.status === "REJECTED" ? "#fecaca" : "#fde68a"}`
                        }}
                      >
                        {loc.status === "APPROVED" ? (loc.isActive ? "✓ Approved & Active" : "🔒 Disabled by Admin") : loc.status === "REJECTED" ? "✕ Rejected" : "● Pending Review"}
                      </span>
                    </div>

                    {/* Address & Contact Details */}
                    <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "8px", margin: "10px 0", fontSize: "12.5px" }}>
                      <div style={{ color: "#0f172a", fontWeight: "600", marginBottom: "4px" }}>
                        📍 {loc.address?.street}{loc.address?.addressLine2 ? `, ${loc.address.addressLine2}` : ""}, {loc.address?.city}, {loc.address?.state} - {loc.address?.pincode}
                      </div>
                      <div style={{ color: "#475569" }}>
                        👤 Contact: <strong>{loc.contactPerson}</strong> &bull; 📞 {loc.contactPhone}
                      </div>
                      <div style={{ color: "#475569", marginTop: "2px" }}>
                        🕒 Hours: {loc.operatingHours}
                      </div>
                      {loc.reason && (
                        <div style={{ color: "#64748b", marginTop: "4px", fontSize: "11.5px", fontStyle: "italic" }}>
                          Vendor Note: "{loc.reason}"
                        </div>
                      )}
                    </div>

                    {loc.status === "REJECTED" && loc.rejectionReason && (
                      <div style={{ padding: "8px 12px", background: "#fef2f2", borderRadius: "6px", fontSize: "12px", color: "#dc2626", fontWeight: "600", marginBottom: "10px" }}>
                        Rejection Reason: {loc.rejectionReason}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons Row */}
                  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px", borderTop: "1px solid #edf0f4", paddingTop: "12px", marginTop: "10px" }}>
                    {loc.status === "PENDING" && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedLocation(loc);
                            setRejectionReason("");
                            setRejectModalOpen(true);
                          }}
                          disabled={actionLoading}
                          className="btn btn-sm btn-outline-danger"
                          style={{ fontSize: "12px", fontWeight: "800", padding: "5px 12px" }}
                        >
                          ✕ Reject
                        </button>
                        <button
                          type="button"
                          onClick={() => handleApprove(loc._id)}
                          disabled={actionLoading}
                          className="btn btn-sm btn-success"
                          style={{ fontSize: "12px", fontWeight: "800", padding: "5px 14px" }}
                        >
                          ✓ Approve Location
                        </button>
                      </>
                    )}

                    {loc.status === "APPROVED" && (
                      <button
                        type="button"
                        onClick={() => handleToggle(loc._id)}
                        disabled={actionLoading}
                        className={`btn btn-sm ${loc.isActive ? "btn-outline-warning" : "btn-outline-success"}`}
                        style={{ fontSize: "12px", fontWeight: "800", padding: "5px 14px" }}
                      >
                        {loc.isActive ? "🔒 Disable Location" : "✓ Enable Location"}
                      </button>
                    )}

                    {loc.status === "REJECTED" && (
                      <button
                        type="button"
                        onClick={() => handleApprove(loc._id)}
                        disabled={actionLoading}
                        className="btn btn-sm btn-outline-success"
                        style={{ fontSize: "12px", fontWeight: "800", padding: "5px 12px" }}
                      >
                        Re-Approve Location
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: "40px", textAlign: "center", color: "#64748b", background: "#ffffff", borderRadius: "14px", border: "1px solid #e2e8f0" }}>
            No pickup locations found matching this filter.
          </div>
        )}

        {/* Reject Location Modal */}
        {rejectModalOpen && selectedLocation && (
          <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 9999, padding: "20px" }}>
            <div style={{ background: "#ffffff", borderRadius: "16px", padding: "26px", maxWidth: "480px", width: "100%", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}>
              <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#dc2626", marginBottom: "4px" }}>
                Reject Pickup Location
              </h3>
              <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
                Location: <strong>{selectedLocation.name}</strong> ({selectedLocation.address?.city})
              </p>

              <form onSubmit={handleReject}>
                <div style={{ marginBottom: "18px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>
                    Mandatory Rejection Reason *
                  </label>
                  <textarea
                    rows="3"
                    required
                    placeholder="State reason why this location cannot be approved..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #f87171", fontSize: "13px" }}
                  />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={() => setRejectModalOpen(false)}
                    className="btn btn-outline-secondary"
                    style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "800" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading || !rejectionReason.trim()}
                    className="btn btn-danger"
                    style={{ padding: "8px 18px", fontSize: "13px", fontWeight: "800" }}
                  >
                    {actionLoading ? "Rejecting..." : "Confirm Rejection"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
